<template>
  <div
      :id="`${source}_recorder`"
      class="flex proxima_nova text-sm text_color font-medium"
  >
    <div
        :id="`${source}_${variants.VARIANT1}_recorder`"
        v-if="type === variants.VARIANT1"
        class="w-full"
    >
      <div
          v-if="state.isRecording"
          :id="`${source}_${variants.VARIANT1}_ongoing_recording`"
          class="flex flex-row gap-2 items-center"
      >
        <div
            class="flex flex-row gap-1 items-center justify-center card_container py-[6px] px-3 w-full"
        >
          <ThemeImage
              class="size-4"
              :icon="imageNames.RECORDING_ICON"
              alt="recording_icon"
          />
          Recording · {{ formatTime(state.recordingTime) }}
        </div>

        <a-button class="secondary_medium" @click="stopRecording">
          <ThemeImage
              class="size-4"
              :icon="imageNames.STOP_RECORDING_GREY"
              alt="stop_recording_icon"
          />
          Stop
        </a-button>
      </div>

      <div
          v-else
          :id="`${source}_${variants.VARIANT1}_waiting_recording`"
          :class="[
          'flex flex-col card_container card_container_bg py-[6px] px-3 w-full items-center',
          { 'cursor-pointer': !state.startRecording },
        ]"
      >
        <span v-if="state.startRecording" class="secondary_color"
        >Recording starts in {{ state.countdown }}...</span
        >
        <div
            v-else
            class="flex flex-row gap-2 items-center font-semibold"
            @click="handleStartRecording"
        >
          <ThemeImage
            class="size-4"
            :icon="imageNames.RECORD_ICON"
            alt="record_icon"
          />
          Start to record
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { variants } from "@/src/constants/customComponentConstants.js";
import { onUnmounted, ref } from "vue";
import {imageNames} from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true, default: "component" },
  type: {
    type: String,
    required: true,
    default: variants.VARIANT1,
    validator: (value) =>
        [variants.VARIANT1, variants.VARIANT2].includes(value),
  },
});

const emit = defineEmits(["recordingComplete"]);

const state = ref({
  startRecording: false,
  isRecording: false,
  countdown: 3,
  recordingTime: 0,
  audioStream: null,
});

let intervals = {
  countdown: null,
  recording: null,
};

let mediaRecorder = null;
let audioChunks = [];

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const clearAllIntervals = () => {
  Object.values(intervals).forEach((interval) => {
    if (interval) clearInterval(interval);
  });
};

const checkBrowserCompatibility = () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.error("getUserMedia is not supported in this browser");
    return false;
  }
  return true;
};

const handleStartRecording = async () => {
  if (!checkBrowserCompatibility()) {
    return;
  }

  try {
    state.value.audioStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100,
      },
    });
    state.value.startRecording = true;
    state.value.countdown = 3;

    intervals.countdown = setInterval(() => {
      state.value.countdown--;
      if (state.value.countdown === 0) {
        clearInterval(intervals.countdown);
        startActualRecording();
      }
    }, 1000);
  } catch (error) {
    console.error("Error accessing microphone:", error);
    state.value.startRecording = false;
  }
};

const startActualRecording = () => {
  const stream = state.value.audioStream;

  // Determine the best supported MIME type
  let mimeType = "audio/webm";
  const supportedMimeTypes = [
    "audio/mp3",
    "audio/mpeg3",
    "audio/mpeg",
    "audio/wav",
    "audio/aac",
    "audio/ogg",
    "audio/x-m4a",
    "audio/x-mpeg-3",
    "audio/webm",
  ];

  for (const type of supportedMimeTypes) {
    if (MediaRecorder.isTypeSupported(type)) {
      mimeType = type;
      break;
    }
  }

  try {
    mediaRecorder = new MediaRecorder(stream, {
      mimeType: mimeType,
      audioBitsPerSecond: 128000,
    });
  } catch (e) {
    console.error("MediaRecorder creation failed:", e);
    mediaRecorder = new MediaRecorder(stream);
  }

  mediaRecorder.ondataavailable = (event) => {
    audioChunks.push(event.data);
  };

  mediaRecorder.onstop = () => {
    const audioBlob = new Blob(audioChunks, { type: mimeType });
    const extension = mimeType.split("/")[1];
    const file = new File([audioBlob], `recording_${Date.now()}.${extension}`, {
      type: mimeType,
      lastModified: Date.now(),
    });
    emit("recordingComplete", file);

    audioChunks = [];
    stream.getTracks().forEach((track) => track?.stop());
  };

  mediaRecorder.start(100);
  state.value.startRecording = false;
  state.value.isRecording = true;
  state.value.recordingTime = 0;

  intervals.recording = setInterval(() => {
    state.value.recordingTime++;
  }, 1000);
};

const stopRecording = () => {
  if (mediaRecorder?.state !== "inactive") {
    mediaRecorder?.stop();
    clearInterval(intervals.recording);
    state.value.isRecording = false;
    state.value.recordingTime = 0;
  }
};

onUnmounted(() => {
  clearAllIntervals();
  if (mediaRecorder?.state !== "inactive") {
    mediaRecorder?.stop();
  }
  if (state.value.audioStream) {
    state.value.audioStream.getTracks().forEach((track) => track.stop());
  }
});
</script>
